import type { PostgrestError } from "@supabase/supabase-js";

export interface PendingRequestInfo {
  label: string;
  start: number;
  requestId: string;
  routeTag: string;
  resourceKey: string;
}

export interface ApiErrorShape {
  code: "TIMEOUT" | "ABORTED" | "REQUEST_ERROR";
  message: string;
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiErrorShape };

interface ApiRunOptions {
  label: string;
  routeTag: string;
  timeoutMs?: number;
  signal?: AbortSignal;
}

interface ApiQueryOptions<T> extends ApiRunOptions {
  run: (signal: AbortSignal) => PromiseLike<{ data: T; error: PostgrestError | Error | null }>;
}

interface ApiFetchOptions extends ApiRunOptions {
  input: RequestInfo | URL;
  init?: RequestInit;
}

const pendingRequests = new Map<string, PendingRequestInfo>();
const latestControllers = new Map<string, { requestId: string; controller: AbortController }>();

function makeRequestId(label: string, routeTag: string) {
  return `${routeTag}:${label}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
}

function toApiError(err: unknown, timedOut: boolean): ApiErrorShape {
  if (timedOut) return { code: "TIMEOUT", message: "TIMEOUT" };
  if (err instanceof DOMException && err.name === "AbortError") {
    return { code: "ABORTED", message: "Request aborted" };
  }
  if (err instanceof Error) {
    return { code: "REQUEST_ERROR", message: err.message || "Request failed" };
  }
  return { code: "REQUEST_ERROR", message: "Request failed" };
}

export function withAbortSignal<T>(query: T, signal: AbortSignal): T {
  const q = query as any;
  if (q && typeof q.abortSignal === "function") {
    return q.abortSignal(signal) as T;
  }
  return query;
}

async function runWithTimeout<T>(
  options: ApiRunOptions,
  runner: (signal: AbortSignal) => Promise<T>
): Promise<ApiResult<T>> {
  const { label, routeTag, timeoutMs = 15_000, signal } = options;
  const requestId = makeRequestId(label, routeTag);
  const start = Date.now();
  const controller = new AbortController();
  const resourceKey = `${routeTag}:${label}`;
  let timedOut = false;

  const previous = latestControllers.get(resourceKey);
  if (previous && !previous.controller.signal.aborted) {
    previous.controller.abort();
    console.log(`[${routeTag}] [${label}] REPLACED`, { prevRequestId: previous.requestId, requestId });
  }
  latestControllers.set(resourceKey, { requestId, controller });

  pendingRequests.set(requestId, { label, start, requestId, routeTag, resourceKey });
  console.log(`[${routeTag}] [${label}] START`, { requestId, timeoutMs });

  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onAbort, { once: true });
  }

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const data = await runner(controller.signal);
    const duration = Date.now() - start;
    console.log(`[${routeTag}] [${label}] END`, { requestId, duration });
    return { ok: true, data };
  } catch (err) {
    if (controller.signal.aborted && !timedOut) {
      const duration = Date.now() - start;
      console.warn(`[${routeTag}] [${label}] ABORTED`, { requestId, duration });
      return { ok: false, error: { code: "ABORTED", message: "Request aborted" } };
    }

    const duration = Date.now() - start;
    const normalized = toApiError(err, timedOut);
    console.error(`[${routeTag}] [${label}] ${timedOut ? "TIMEOUT" : "ERROR"}`, {
      requestId,
      duration,
      code: normalized.code,
      message: normalized.message,
    });
    return { ok: false, error: normalized };
  } finally {
    clearTimeout(timer);
    pendingRequests.delete(requestId);
    if (signal) signal.removeEventListener("abort", onAbort);

    const latest = latestControllers.get(resourceKey);
    if (latest?.requestId === requestId) {
      latestControllers.delete(resourceKey);
    }
  }
}

export const apiClient = {
  query: async <T>(options: ApiQueryOptions<T>): Promise<ApiResult<T>> => {
    return runWithTimeout<T>(options, async (abortSignal) => {
      const result = await Promise.resolve(options.run(abortSignal));
      if (result.error) throw result.error;
      return result.data;
    });
  },

  fetch: async (options: ApiFetchOptions): Promise<ApiResult<Response>> => {
    return runWithTimeout<Response>(options, async (abortSignal) => {
      const response = await fetch(options.input, {
        ...options.init,
        signal: abortSignal,
      });
      if (!response.ok) {
        throw new Error(`HTTP_${response.status}`);
      }
      return response;
    });
  },
};

export function unwrapApiResult<T>(result: ApiResult<T>): T {
  if ("error" in result) {
    const message = result.error.code === "TIMEOUT" ? "TIMEOUT" : result.error.message;
    const err = new Error(message);
    (err as Error & { code?: string }).code = result.error.code;
    throw err;
  }
  return result.data;
}

export function getApiPendingRequests(): PendingRequestInfo[] {
  return Array.from(pendingRequests.values());
}

export function getApiStuckRequests(thresholdMs = 8_000): PendingRequestInfo[] {
  const now = Date.now();
  return getApiPendingRequests().filter((r) => now - r.start > thresholdMs);
}

